import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Command {
  id: string;
  title: string;
  description?: string;
  action: () => void;
  shortcut?: string;
}

export function useCommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const commands: Command[] = [
    {
      id: 'owner',
      title: 'Owner Dashboard',
      description: 'View KPIs and metrics',
      action: () => navigate('/manage/owner'),
      shortcut: 'g o',
    },
    {
      id: 'data',
      title: 'Data Hub',
      description: 'Manage owner pack and webhooks',
      action: () => navigate('/manage/data'),
      shortcut: 'g d',
    },
    {
      id: 'inventory',
      title: 'Inventory',
      description: 'Upload and manage domains',
      action: () => navigate('/manage/inventory'),
      shortcut: 'g i',
    },
    {
      id: 'prompts',
      title: 'Prompts',
      description: 'Audit prompt history',
      action: () => navigate('/manage/prompts'),
      shortcut: 'g p',
    },
    {
      id: 'settings',
      title: 'Settings',
      description: 'Configure admin preferences',
      action: () => navigate('/manage/settings'),
      shortcut: 'g s',
    },
    {
      id: 'health',
      title: 'Health Check',
      description: 'View system status',
      action: () => navigate('/health'),
    },
  ];

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Command palette toggle (Ctrl/Cmd + K)
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
        return;
      }

      // Escape to close
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setSearch('');
        return;
      }

      // Keyboard shortcuts (only when palette is closed)
      if (!isOpen && e.key === 'g') {
        const handleSecondKey = (e2: KeyboardEvent) => {
          const shortcut = `g ${e2.key}`;
          const command = commands.find((c) => c.shortcut === shortcut);
          if (command) {
            e2.preventDefault();
            command.action();
          }
          document.removeEventListener('keydown', handleSecondKey);
        };
        document.addEventListener('keydown', handleSecondKey);
        setTimeout(() => {
          document.removeEventListener('keydown', handleSecondKey);
        }, 1000);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const filteredCommands = search
    ? commands.filter(
        (cmd) =>
          cmd.title.toLowerCase().includes(search.toLowerCase()) ||
          cmd.description?.toLowerCase().includes(search.toLowerCase()),
      )
    : commands;

  return {
    isOpen,
    setIsOpen,
    search,
    setSearch,
    commands: filteredCommands,
    executeCommand: (command: Command) => {
      command.action();
      setIsOpen(false);
      setSearch('');
    },
  };
}
